import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {DataObjectWithRelated} from "@cm/utils"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"

const TD = TypeDescriptors

const appendDataObjectSizeDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    input: TD.inlet(TD.DataObject),
    output: TD.outlet(TD.DataObject),
}

export class AppendDataObjectSize implements NodeClassImpl<typeof appendDataObjectSizeDescriptor, typeof AppendDataObjectSize> {
    static descriptor = appendDataObjectSizeDescriptor
    static uniqueName = "AppendDataObjectSize"
    sceneManager!: Inlet<ISceneManager>
    input!: Inlet<DataObjectWithRelated>
    output!: Outlet<DataObjectWithRelated>

    private pending: Subscription | null = null

    run() {
        this.cleanup()

        if (this.sceneManager === NotReady || this.input === NotReady) {
            this.output.emitIfChanged(NotReady)
            return
        }

        const {input, sceneManager} = this

        const {size} = input
        if (size !== undefined && size !== null) {
            this.output.emitIfChanged(input)
            return
        }

        const appendDataObjectSize = async () => {
            const size = await sceneManager.getDataObjectSize(input)
            return {
                ...input,
                size,
            }
        }

        this.pending = sceneManager.addTask({
            description: `appendDataObjectSize(${input.legacyId})`,
            task: defer(() => appendDataObjectSize()).pipe(
                map((data) => {
                    this.output.emitIfChanged(data)
                }),
            ),
            critical: false,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
